import { FieldPath, FieldValues, useController } from "react-hook-form";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { RhfInputProps } from "../../../../../Forms/RhfInputProps";
import { RHFFieldErrorText } from "../../../../../Forms/RHFFieldErrorText";
import { OptionalLabelProps } from "../../../../../Forms/OptionalLabelProps";
import { SelectionListItem } from "../../../../../Forms/SelectionListItem";
import { MuiSelectChip } from "../../../../../Layout/Select/MuiSelectChip";

type RHFAutoCompleteProps = {
  initialFilters: SelectionListItem<string>[] | undefined;
  apiUrl: string;
  errorMessage: string;
};

export const RHFAutoCompleteWithDebouncedFetch = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  control,
  name,
  initialFilters,
  apiUrl,
  errorMessage,
  label,
}: RhfInputProps<TFieldValues, TName> & RHFAutoCompleteProps & OptionalLabelProps) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const [filtersFromSearch, setFiltersFromSearch] = useState<SelectionListItem<string>[]>([]);

  const fetchFiltersDebounced = useMemo(
    () =>
      debounce(async (search: string) => {
        if (!search) return;

        await axios
          .get<SelectionListItem<string>[]>(apiUrl, {
            params: { search: search.toUpperCase() },
          })
          .then((response) => setFiltersFromSearch(response.data))
          .catch(() => toast.error(errorMessage));
      }, 750),
    [apiUrl, errorMessage]
  );

  const [selectedFilters, setSelectedFilters] = useState<SelectionListItem<string>[]>([]);
  useEffect(
    () => setSelectedFilters((prev) => [...prev, ...(initialFilters || [])]),
    [initialFilters]
  );

  function updateSelectedFiltersWith(selectedValues: string[]) {
    setSelectedFilters((previouslySelectedFilters) =>
      previouslySelectedFilters
        .concat(filtersFromSearch)
        .filter((item) => selectedValues.includes(item.Id))
    );
  }

  const options = filtersFromSearch.concat(selectedFilters);
  const labels = Object.fromEntries(options.map((option) => [option.Id, option.Label]));

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        {...field}
        multiple
        options={options.map((it) => it.Id)}
        noOptionsText="Please start typing to search for options"
        getOptionLabel={(optionId) => labels[optionId] ?? "NOT FOUND"}
        onChange={(_, selectedValues) => {
          updateSelectedFiltersWith(selectedValues);
          field.onChange(selectedValues);
        }}
        onInputChange={(_, value) => fetchFiltersDebounced(value)}
        renderInput={(params) => (
          <TextField {...params} InputLabelProps={{ shrink: true }} label={label} />
        )}
        renderTags={(values, getTagProps) =>
          values.map((value, index) => (
            <MuiSelectChip
              getTagProps={getTagProps}
              labels={labels}
              index={index}
              valueId={value}
            />
          ))
        }
        size="small"
        filterSelectedOptions
        disableCloseOnSelect
      />
      {!!error && <RHFFieldErrorText fieldError={error} />}
    </FormControl>
  );
};
